import { FormattedMessage } from 'react-intl';
import {
    PoolButton,
    PoolModal,
    PoolModalActions,
    PoolModalContent,
    PoolModalTitle,
    PoolSelect,
    PoolSelectOption,
    useIsMobile,
} from '@synergy/components-next';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';

import { WishListId } from '../../../models/wish-list-list-item';
import { WishListItem } from '../../../models/wish-list';
import { WishListType } from '../../../models/enums/wish-list-type';
import { moveWishListItem } from '../../../store/whish-list-actions';
import { useAppDispatch, useAppSelector } from '../../../../store/store';
import { wishListsSelector } from '../../../store/whish-list-selectors';

export interface WishListMoveItemModalProps {
    open: boolean;
    wishListItem: WishListItem | null;
    onClose: () => void;
}

export const WishListMoveItemModal = (props: WishListMoveItemModalProps): ReactElement => {
    const dispatch = useAppDispatch();
    const allWishlists = useAppSelector(wishListsSelector);
    const [selectedListId, setSelectedListId] = useState<WishListId>('default');
    const { id: paramId = '' } = useParams<{ id?: string }>();
    const wishListId = +paramId || 'default';
    const wishLists = useMemo(() => {
        return allWishlists.filter(
            list =>
                (list.id !== wishListId || list.isDefault) &&
                (list.isOwner || list.type === WishListType.OrganizationEdit)
        );
    }, [allWishlists, wishListId]);
    const isMobile = useIsMobile();

    useEffect(() => {
        setSelectedListId(wishLists[0]?.id);
    }, [wishLists]);

    const handleMove = (): void => {
        if (props.wishListItem) {
            dispatch(moveWishListItem(wishListId, props.wishListItem?.id, selectedListId));
        }
        props.onClose();
    };

    const options = wishLists.map<PoolSelectOption<WishListId>>(list => ({
        value: list.id,
        displayValue: list.name,
    }));

    return (
        <PoolModal open={props.open} onClose={props.onClose}>
            <PoolModalTitle>
                <FormattedMessage id="app.wishList.move" />
            </PoolModalTitle>
            <PoolModalContent>
                <PoolSelect
                    fullWidth={true}
                    options={options}
                    value={selectedListId}
                    label={<FormattedMessage id="app.wishList.labelMove" />}
                    onChange={e => setSelectedListId(+e.target.value || 'default')}
                />
            </PoolModalContent>
            <PoolModalActions>
                <PoolButton variant="outlined" onClick={props.onClose}>
                    <FormattedMessage id="app.common.cancel" />
                </PoolButton>
                <PoolButton onClick={handleMove}>
                    {isMobile ? (
                        <FormattedMessage id="app.wishList.move.mobile" />
                    ) : (
                        <FormattedMessage id="app.wishList.move" />
                    )}
                </PoolButton>
            </PoolModalActions>
        </PoolModal>
    );
};
